import { useMemo } from "react";
import { useQuery } from "@apollo/client";

import { showApiKeysInfoDrawer } from "components/ApiKeys/InfoDrawer";
import NotFoundPage from "components/error/NotFoundPage";
import ListContentWrapper from "components/ListContentWrapper";
import PageLayoutSkeleton from "components/PageLayoutSkeleton";
import TableSimple from "ds/components/TableSimple";
import { AnalyticsPagePersonal } from "hooks/useAnalytics/pages/personal";
import useErrorHandle from "hooks/useErrorHandle";
import { AuthorizationScheme } from "types/generated";
import { UserManagementActivationStatus } from "views/Account/Settings/types";

import PersonalSettingsApiKeysPageLayout from "./components/PageLayout";
import { showPersonalSettingsApiKeysCreateDrawer } from "./CreateDrawer";
import PersonalSettingsApiKeysEmpty from "./Empty";
import { GET_PERSONAL_API_KEYS } from "./gql";
import { PERSONAL_API_KEYS_TABLE_CELLS } from "./Table/Cells";
import {
  PERSONAL_API_KEYS_FILTERS_ORDER_KEY,
  PERSONAL_API_KEYS_SORT_CONFIG,
} from "./Table/constants";
import usePersonalApiKeysSort from "./Table/useSort";
import usePersonalApiKeysTableStore from "./Table/useTableStore";

type PersonalSettingsApiKeysProps = {
  authorizationScheme: string;
};

const REFETCH_QUERIES = [GET_PERSONAL_API_KEYS];

const PersonalSettingsApiKeys = ({ authorizationScheme }: PersonalSettingsApiKeysProps) => {
  const { data, loading, error } = useQuery(GET_PERSONAL_API_KEYS, {
    fetchPolicy: "no-cache",
  });

  const activationStatus =
    authorizationScheme === AuthorizationScheme.ManagedUsers
      ? UserManagementActivationStatus.ACTIVE
      : UserManagementActivationStatus.INACTIVE;

  const handleOpenInfoDrawer = () => {
    showApiKeysInfoDrawer({
      analyticsPage: AnalyticsPagePersonal.PersonalApiKeys,
    });
  };

  const handleCreateApiKeyDrawer = () => {
    showPersonalSettingsApiKeysCreateDrawer({
      refetchQueries: REFETCH_QUERIES,
    });
  };

  const apiKeys = useMemo(() => data?.viewer?.ownedApiKeys || [], [data?.viewer?.ownedApiKeys]);
  const sortedApiKeys = usePersonalApiKeysSort(apiKeys);

  const isManagedUsersActive = activationStatus === UserManagementActivationStatus.ACTIVE;
  const shouldShowManagedUsersWarning = !isManagedUsersActive;

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !data?.viewer?.ownedApiKeys) {
    return (
      <PersonalSettingsApiKeysPageLayout showManagedUsersWarning={shouldShowManagedUsersWarning}>
        <PageLayoutSkeleton />
      </PersonalSettingsApiKeysPageLayout>
    );
  }

  if (!data?.viewer?.ownedApiKeys) {
    return <NotFoundPage />;
  }

  return (
    <PersonalSettingsApiKeysPageLayout
      onOpenInfoDrawer={handleOpenInfoDrawer}
      onOpenCreateApiKeyDrawer={handleCreateApiKeyDrawer}
      seats={data.seats.apiKey}
      showManagedUsersWarning={shouldShowManagedUsersWarning}
    >
      <ListContentWrapper>
        {shouldShowManagedUsersWarning && <PersonalSettingsApiKeysEmpty isUnavailable />}
        {isManagedUsersActive && apiKeys.length === 0 && (
          <PersonalSettingsApiKeysEmpty
            onCreate={handleCreateApiKeyDrawer}
            seats={data.seats.apiKey}
          />
        )}
        {isManagedUsersActive && apiKeys.length > 0 && (
          <TableSimple
            data={sortedApiKeys}
            store={usePersonalApiKeysTableStore}
            ariaLabel="Personal API keys table"
            cellPerColumn={PERSONAL_API_KEYS_TABLE_CELLS}
            sortConfig={PERSONAL_API_KEYS_SORT_CONFIG}
            filtersStorageKey={PERSONAL_API_KEYS_FILTERS_ORDER_KEY}
            hideSettings
            hasPagination={false}
          />
        )}
      </ListContentWrapper>
    </PersonalSettingsApiKeysPageLayout>
  );
};

export default PersonalSettingsApiKeys;
